import type { BulkChange } from '@/composables/api/useBulkActionApi';
import { DAY_IDS } from './constants';
import { emptyScheduleForm, kindUsesOrdering, kindUsesPriority, SCHEDULE_FORM_MESSAGES, type EditableScheduleKind, type ScheduleForm } from './form';
import { ORDERING, orderingLabel, orderingToColumns, type OrderingValue } from './ordering';
import { formatClockTime, formatDayNames } from './summary';
import { encodeDayIds, normalizeClockTime, positiveInteger } from './values';
import { isEndBeforeStart } from '@/utils/date';

/**
 * The schedule editor's form, used for a bulk edit of one kind of schedule.
 *
 * The bulk edit dialog has one tab per kind in the selection. Each tab is the editor's own form,
 * but every field starts as "Keep as is" and only the fields the user changes are sent. A tab's
 * changes carry its kind, so they reach only rows of that kind.
 *
 * The values live in an ordinary {@link ScheduleForm}, so the editor's controls edit them as they
 * do for one schedule. What bulk adds sits beside it: which fields are changed, and for a date
 * whether it is set, moved by some days, or (for an end date) removed.
 */

export type BulkScheduleField =
    | 'ordering'
    | 'priority'
    | 'startTime'
    | 'endTime'
    | 'dayIds'
    | 'repeatWeeks'
    | 'startDate'
    | 'endDate'
    | 'frequency'
    | 'rampDays'
    | 'repeatMinutes'
    | 'completeTrack'
    | 'ignoreDlWindow'
    | 'revertToDaily'
    | 'startExpression'
    | 'endExpression';

/** What a bulk edit does to a date: pick one, move it by some days, or (an end date) remove it. */
export type BulkDateAction = 'set' | 'shift' | 'clear';

export interface ScheduleBulkState {
    changed: Partial<Record<BulkScheduleField, boolean>>;
    startDateAction: Exclude<BulkDateAction, 'clear'>;
    endDateAction: BulkDateAction;
    startShiftDays: number | null;
    endShiftDays: number | null;
    /** Priority set to none, which the editor shows as an empty box. */
    priorityNone: boolean;
    completeTrack: boolean;
    ignoreDlWindow: boolean;
    revertToDaily: boolean;
    startExpression: string;
    endExpression: string;
}

export interface ScheduleBulkTab {
    kind: EditableScheduleKind;
    form: ScheduleForm;
    bulk: ScheduleBulkState;
}

export interface ScheduleBulkError {
    field: BulkScheduleField;
    message: string;
}

/** The server's own names for the editor's start and end date, resolved per row (see the API spec). */
export const ROW_START_DATE = 'row_start_date';
export const ROW_END_DATE = 'row_end_date';

/** A tab with nothing changed yet. The values are the editor's defaults for a new schedule. */
export function emptyScheduleBulkTab(kind: EditableScheduleKind): ScheduleBulkTab {
    return {
        kind,
        form: { ...emptyScheduleForm(kind), occurrence: kind === 'recurring' ? 'recurring' : 'single' },
        bulk: {
            changed: {},
            startDateAction: 'set',
            endDateAction: 'set',
            startShiftDays: null,
            endShiftDays: null,
            priorityNone: false,
            completeTrack: false,
            ignoreDlWindow: false,
            revertToDaily: false,
            startExpression: '',
            endExpression: '',
        },
    };
}

/**
 * The fields a tab offers, in the editor's order. These are the fields the editor shows for the
 * kind, less the ones bulk edit cannot change: the type (the tab is the type) and the occurrence
 * (switching between weekly and dated would move a row's dates to other columns).
 */
export function bulkFieldsForKind(kind: EditableScheduleKind): BulkScheduleField[] {
    const fields: BulkScheduleField[] = [];
    if (kindUsesOrdering(kind)) fields.push('ordering');
    if (kindUsesPriority(kind)) fields.push('priority');
    if (kind === 'insert') fields.push('frequency', 'rampDays');
    if (kind === 'message' || kind === 'silence') fields.push('repeatMinutes');
    fields.push('startTime', 'endTime');
    if (kind === 'recurring') fields.push('dayIds', 'repeatWeeks');
    fields.push('startDate', 'endDate', 'completeTrack', 'ignoreDlWindow', 'revertToDaily', 'startExpression', 'endExpression');
    return fields;
}

/** An end date can be removed on every kind but a one-off, which the editor never leaves open-ended. */
export function kindAllowsNoEndDate(kind: EditableScheduleKind): boolean {
    return kind !== 'oneoff';
}

export function changedFields(tab: ScheduleBulkTab): BulkScheduleField[] {
    return bulkFieldsForKind(tab.kind).filter((field) => tab.bulk.changed[field]);
}

function isWholeAtLeastOne(value: number | null | undefined): boolean {
    return typeof value === 'number' && Number.isInteger(value) && value >= 1;
}

function isShift(value: number | null): boolean {
    return typeof value === 'number' && Number.isInteger(value) && value !== 0 && Math.abs(value) <= 366;
}

const SHIFT_INVALID = 'Move by a whole number of days, from -366 to 366, and not 0.';

/** The editor's own checks, on the fields the tab changes. */
export function validateScheduleBulkTab(tab: ScheduleBulkTab): ScheduleBulkError[] {
    const { form, bulk } = tab;
    const errors: ScheduleBulkError[] = [];
    const add = (field: BulkScheduleField, message: string) => errors.push({ field, message });

    for (const field of changedFields(tab)) {
        switch (field) {
            case 'priority':
                if (!bulk.priorityNone && !isWholeAtLeastOne(form.priority)) add(field, SCHEDULE_FORM_MESSAGES.PRIORITY_INVALID);
                break;
            case 'startTime':
                if (!normalizeClockTime(form.startTime)) add(field, SCHEDULE_FORM_MESSAGES.START_TIME_REQUIRED);
                break;
            case 'endTime':
                if (!normalizeClockTime(form.endTime)) add(field, SCHEDULE_FORM_MESSAGES.END_TIME_REQUIRED);
                break;
            case 'dayIds':
                if (form.dayIds.length === 0) add(field, SCHEDULE_FORM_MESSAGES.DAYS_REQUIRED);
                break;
            case 'repeatWeeks':
                if (!isWholeAtLeastOne(form.repeatWeeks)) add(field, SCHEDULE_FORM_MESSAGES.REPEAT_WEEKS_INVALID);
                break;
            case 'startDate':
                if (bulk.startDateAction === 'set' && !form.startDate) add(field, SCHEDULE_FORM_MESSAGES.START_DATE_REQUIRED);
                if (bulk.startDateAction === 'shift' && !isShift(bulk.startShiftDays)) add(field, SHIFT_INVALID);
                break;
            case 'endDate':
                if (bulk.endDateAction === 'set' && !form.endDate) add(field, 'Choose an end date.');
                if (bulk.endDateAction === 'shift' && !isShift(bulk.endShiftDays)) add(field, SHIFT_INVALID);
                break;
            case 'frequency':
                if (!isWholeAtLeastOne(form.fromTracks) || !isWholeAtLeastOne(form.toTracks)) add(field, SCHEDULE_FORM_MESSAGES.TRACKS_INVALID);
                else if (form.fromTracks < form.toTracks) add(field, SCHEDULE_FORM_MESSAGES.FROM_BELOW_TO);
                break;
            case 'rampDays':
                if (!isWholeAtLeastOne(form.rampDays)) add(field, SCHEDULE_FORM_MESSAGES.RAMP_INVALID);
                break;
            case 'repeatMinutes':
                if (!isWholeAtLeastOne(form.repeatMinutes)) add(field, SCHEDULE_FORM_MESSAGES.MINUTES_INVALID);
                break;
            default:
                break;
        }
    }

    if (bulkTabEndsBeforeStart(tab)) add('endDate', SCHEDULE_FORM_MESSAGES.END_DATE_BEFORE_START);
    return errors;
}

/**
 * The start date this tab sets on every row, or null when it keeps or moves each row's own.
 * The end date picker starts there.
 */
export function bulkTabSetStartDate(tab: Pick<ScheduleBulkTab, 'form' | 'bulk'>): string | null {
    return tab.bulk.changed.startDate && tab.bulk.startDateAction === 'set' ? tab.form.startDate : null;
}

/**
 * Whether the tab sets both dates, with the end before the start. That is wrong for every row,
 * so the dialog refuses it. When only one date is set, or a date is moved, each row's own other
 * date decides, and the server checks every row and lists the ones it can't change.
 */
export function bulkTabEndsBeforeStart(tab: Pick<ScheduleBulkTab, 'form' | 'bulk'>): boolean {
    return Boolean(tab.bulk.changed.endDate) && tab.bulk.endDateAction === 'set' && isEndBeforeStart(bulkTabSetStartDate(tab), tab.form.endDate);
}

/** One tab's changed fields as bulk changes, each limited to the tab's kind. */
export function scheduleBulkTabToChanges(tab: ScheduleBulkTab): BulkChange[] {
    const { form, bulk, kind } = tab;
    const changes: BulkChange[] = [];
    const set = (field: string, value: BulkChange['value']) => changes.push({ field, op: 'set', value, kinds: [kind] });

    for (const field of changedFields(tab)) {
        switch (field) {
            case 'ordering': {
                const columns = orderingToColumns(form.ordering);
                for (const column of ['should_shuffle', 'resume_from_file_offset', 'loop'] as const) {
                    if (columns[column] !== undefined) set(column, columns[column]!);
                }
                break;
            }
            case 'priority':
                if (bulk.priorityNone) changes.push({ field: 'priority', op: 'clear', value: null, kinds: [kind] });
                else set('priority', form.priority);
                break;
            case 'startTime':
                set('time', normalizeClockTime(form.startTime));
                break;
            case 'endTime':
                set('end_time', normalizeClockTime(form.endTime));
                break;
            case 'dayIds':
                set('delimited_day_ids', encodeDayIds(form.dayIds));
                break;
            case 'repeatWeeks':
                set('recurring_weekly_interval', form.repeatWeeks);
                break;
            case 'startDate':
                if (bulk.startDateAction === 'shift') changes.push({ field: ROW_START_DATE, op: 'shift_days', value: bulk.startShiftDays, kinds: [kind] });
                else set(ROW_START_DATE, form.startDate);
                break;
            case 'endDate':
                if (bulk.endDateAction === 'shift') changes.push({ field: ROW_END_DATE, op: 'shift_days', value: bulk.endShiftDays, kinds: [kind] });
                else if (bulk.endDateAction === 'clear') changes.push({ field: ROW_END_DATE, op: 'clear', value: null, kinds: [kind] });
                else set(ROW_END_DATE, form.endDate);
                break;
            case 'frequency':
                set('insert_frequency_tracks_start', form.fromTracks);
                set('insert_frequency_tracks_target', form.toTracks);
                break;
            case 'rampDays':
                set('insert_frequency_ramp_days', form.rampDays);
                break;
            case 'repeatMinutes':
                set('messaging_frequency_minutes', positiveInteger(form.repeatMinutes));
                break;
            case 'completeTrack':
                set('complete_track', bulk.completeTrack);
                break;
            case 'ignoreDlWindow':
                set('ignore_dl_window', bulk.ignoreDlWindow);
                break;
            case 'revertToDaily':
                set('revert_to_daily', bulk.revertToDaily);
                break;
            case 'startExpression':
                if (bulk.startExpression.trim() === '') changes.push({ field: 'natural_language_date_expression', op: 'clear', value: null, kinds: [kind] });
                else set('natural_language_date_expression', bulk.startExpression.trim());
                break;
            case 'endExpression':
                if (bulk.endExpression.trim() === '') changes.push({ field: 'natural_language_end_date_expression', op: 'clear', value: null, kinds: [kind] });
                else set('natural_language_end_date_expression', bulk.endExpression.trim());
                break;
        }
    }
    return changes;
}

/** The labels the editor puts on its fields, as the confirmation names them. */
export const BULK_SCHEDULE_FIELD_LABELS: Record<BulkScheduleField, string> = {
    ordering: 'Ordering',
    priority: 'Priority',
    startTime: 'Start time',
    endTime: 'End time',
    dayIds: 'Repeat on',
    repeatWeeks: 'Repeat every',
    startDate: 'Start date',
    endDate: 'End date',
    frequency: 'Frequency',
    rampDays: 'Ramp (days)',
    repeatMinutes: 'Minutes between plays',
    completeTrack: 'Complete track',
    ignoreDlWindow: 'Ignore DL window',
    revertToDaily: 'Revert to daily',
    startExpression: 'Start date expression',
    endExpression: 'End date expression',
};

/** "2026-11-08" as 11/08/26, the way the app prints a plain date. */
function plainDate(date: string | null): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date ?? '');
    return match ? `${match[2]}/${match[3]}/${match[1]!.slice(-2)}` : (date ?? '');
}

function moveText(label: string, days: number | null): string {
    const size = Math.abs(days ?? 0);
    return `Move the ${label.toLowerCase()} ${(days ?? 0) > 0 ? 'forward' : 'back'} ${size} day${size === 1 ? '' : 's'}`;
}

/** One line per changed field, in the editor's words, for the confirmation. */
export function describeScheduleBulkTab(tab: ScheduleBulkTab): string[] {
    const { form, bulk } = tab;
    const yesNo = (value: boolean) => (value ? 'Yes' : 'No');
    return changedFields(tab).map((field) => {
        const label = BULK_SCHEDULE_FIELD_LABELS[field];
        switch (field) {
            case 'ordering':
                return `Set ${label} to ${ORDERING.some((option) => option.value === form.ordering) ? orderingLabel(form.ordering as OrderingValue) : form.ordering}`;
            case 'priority':
                return bulk.priorityNone ? 'Remove the priority' : `Set ${label} to ${form.priority}`;
            case 'startTime':
                return `Set ${label} to ${formatClockTime(normalizeClockTime(form.startTime)) ?? form.startTime}`;
            case 'endTime':
                return `Set ${label} to ${formatClockTime(normalizeClockTime(form.endTime)) ?? form.endTime}`;
            case 'dayIds':
                return `Set ${label} to ${form.dayIds.length === DAY_IDS.length ? 'Every day' : formatDayNames(form.dayIds)}`;
            case 'repeatWeeks':
                return `Set ${label} to ${form.repeatWeeks} ${form.repeatWeeks === 1 ? 'week' : 'weeks'}`;
            case 'startDate':
                return bulk.startDateAction === 'shift' ? moveText(label, bulk.startShiftDays) : `Set ${label} to ${plainDate(form.startDate)}`;
            case 'endDate':
                if (bulk.endDateAction === 'shift') return moveText(label, bulk.endShiftDays);
                return bulk.endDateAction === 'clear' ? 'Remove the end date' : `Set ${label} to ${plainDate(form.endDate)}`;
            case 'frequency':
                return `Set ${label} to from every ${form.fromTracks} to every ${form.toTracks} tracks`;
            case 'rampDays':
                return `Set ${label} to ${form.rampDays}`;
            case 'repeatMinutes':
                return `Set ${label} to ${form.repeatMinutes}`;
            case 'completeTrack':
                return `Set ${label} to ${yesNo(bulk.completeTrack)}`;
            case 'ignoreDlWindow':
                return `Set ${label} to ${yesNo(bulk.ignoreDlWindow)}`;
            case 'revertToDaily':
                return `Set ${label} to ${yesNo(bulk.revertToDaily)}`;
            case 'startExpression':
                return bulk.startExpression.trim() === '' ? `Clear the ${label.toLowerCase()}` : `Set ${label} to “${bulk.startExpression.trim()}”`;
            case 'endExpression':
                return bulk.endExpression.trim() === '' ? `Clear the ${label.toLowerCase()}` : `Set ${label} to “${bulk.endExpression.trim()}”`;
        }
    });
}
